import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { registerFirmOAuth } from '../utils/apiUtils';
import { showErrorMessage, showSuccessMessage } from '@/shared/components/toast-system';
import { validateField, isValidPhone } from '../utils/validationUtils';
import { getUserReadableError } from '../utils/utils';
import { useDispatch } from 'react-redux';
import { login } from '../redux/authSlice';
import AuthLayout from '../components/AuthLayout';

class RegisterOAuth extends React.Component {

  constructor(props) {
    super(props);

    const { firstName = '', lastName = '' } = props.location?.state || {};

    this.state = {
      formData: {
        firmName: '',
        firstName,
        lastName,
        phone: '',
        timezone: 'America/New_York',
        dateFormat: 'MM-DD-YYYY',
      },
      isSubmitting: false,
      hasAcceptedPrivacy: false,
    };
  }

  handleInputChange = (e) => {
    const { name, value } = e.target;
    this.setState((prevState) => ({
      formData: {
        ...prevState.formData,
        [name]: value,
      },
    }));
  };

  onSubmit = async (e) => {
    e.preventDefault();
    this.setState({ isSubmitting: true });
    const code = new URLSearchParams(this.props.location.search).get('code');

    let errors = [];
    let error = validateField({ isRequired: true, maxLength: 100 }, this.state.formData.firmName, 'Firm Name');
    if (!!error) errors.push(error);

    const trimmedPhone = this.state.formData.phone.trim();
    if (trimmedPhone && !isValidPhone(trimmedPhone)) {
      errors.push('Please enter a valid phone number');
    }

    if (!!errors?.length) {
      showErrorMessage(errors[0]);
      this.setState({ isSubmitting: false });
      return;
    }

    if (!this.state.hasAcceptedPrivacy) {
      showErrorMessage('Please confirm that you have read and agree to the Terms and Privacy Policy.');
      this.setState({ isSubmitting: false });
      return;
    }

    try {
      const registerResponse = await registerFirmOAuth({
        ...this.state.formData,
        phone: trimmedPhone || null,
        code,
        privacyPolicyAccepted: this.state.hasAcceptedPrivacy,
      });
      showSuccessMessage('Your account has been activated successfully.');

      const user = registerResponse.data?.user;
      if (user) {
        this.props.dispatch(login(user));
      }

      const pendoParams = '?pendo=LA7k-x1xoQDk3OBPh1pRGoe5kU0&step=Z6U9lrOAqIVyLsjvA6_8071mRGY';
      setTimeout(() => { this.props.navigate('/onboarding' + pendoParams); }, 50);

    } catch (error) {
      console.error('OAuth Registration failed:', error);
      const errorMessage = getUserReadableError(error) || 'An unexpected error occurred.';
      showErrorMessage(errorMessage);
    } finally {
      this.setState({ isSubmitting: false });
    }
  };

  render() {
    const { isSubmitting, formData } = this.state;
    return (
      <AuthLayout
        icon={<img src="/images/logo_trans.png" alt="TypeHero" className="auth-logo" />}
        title="Complete registration"
        description="Just one more step. Please provide the following details to set up your firm."
        wide
      >
        <form onSubmit={this.onSubmit} className="auth-form" noValidate>

          <div className="input-row" style={{ gap: '12px' }}>
            <div className="input-group">
              <label htmlFor="firstName" className="input-label">First name</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                readOnly
                className="input-field"
                style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
              />
            </div>
            <div className="input-group">
              <label htmlFor="lastName" className="input-label">Last name</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                readOnly
                className="input-field"
                style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
              />
            </div>
          </div>

          <div className="input-group">
            <label htmlFor="firmName" className="input-label">Firm name</label>
            <input
              type="text"
              id="firmName"
              name="firmName"
              required
              value={formData.firmName}
              onChange={this.handleInputChange}
              autoComplete="organization"
              className="input-field"
            />
          </div>

          <div className="input-group">
            <label htmlFor="phone" className="input-label">Phone number (optional)</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={this.handleInputChange}
              autoComplete="tel"
              className="input-field"
            />
          </div>

          <div className="auth-checkbox-group">
            <input
              type="checkbox"
              id="privacyConsent"
              checked={this.state.hasAcceptedPrivacy}
              onChange={(e) => this.setState({ hasAcceptedPrivacy: e.target.checked })}
              required
              className="auth-checkbox"
            />
            <label htmlFor="privacyConsent" className="auth-checkbox-label">
              I have read and agree to the{' '}
              <a className="auth-inline-link" href="https://www.typehero.ai/terms" target="_blank" rel="noreferrer">
                Terms
              </a>{' '}and{' '}
              <a className="auth-inline-link" href="https://www.typehero.ai/privacy-policy" target="_blank" rel="noreferrer">
                Privacy Policy
              </a>.
            </label>
          </div>

          <div className="pt-2">
            <button type="submit" className="auth-button" disabled={isSubmitting}>
              {isSubmitting ? 'Completing registration...' : 'Submit'}
            </button>
          </div>

        </form>
      </AuthLayout>
    );
  }
}

function withRouter(Component) {
  return function WrappedComponent(props) {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    return <Component {...props} navigate={navigate} location={location} dispatch={dispatch} />;
  };
}

export default withRouter(RegisterOAuth);